import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider, LearningProgressProvider } from "./contexts/AuthContext";
import { useAuth } from "./contexts/AuthContext";
import useRouteState from "./hooks/useRouteState";
import { Role } from "./constants/Role";
import "./styles/App.css";
import AppRoutes from "../src/routers/AppRoutes";
import Navbar from "components/Navbar";
import Footer from "components/Footer";
import CartApiService from './services/CartApiService';

const AppContent = () => {
  const { loading, initialized, authenticated, userRoles, hasAnyRole } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useRouteState();

  useEffect(() => {
    if (initialized && authenticated) {
      // Chỉ redirect mentor về /mentor/courses nếu đang ở trang gốc hoặc trang không hợp lệ
      if (
        hasAnyRole([Role.STAFF_COURSE_MENTOR]) &&
        !location.pathname.startsWith('/mentor/courses') &&
        !location.pathname.startsWith('/learningzone/')
      ) {
        navigate("/mentor/courses", { replace: true });
        return;
      }
      // Logic cũ cho admin/staff
      const adminRoles = [
        Role.ADMIN_DEV,
        Role.ADMIN_BUSINESS,
        Role.STAFF_ACCOUNTING,
        Role.STAFF_COURSE_MANAGER,
      ];
      if (hasAnyRole(adminRoles)) {
        const adminAllowedRoutes = [
          "/profile",
          "/admin",
          "/manage/categories",
          "/manage/courses",
          "/manage/orders",
          "/course-builder"
        ];
        if (!adminAllowedRoutes.some(route => location.pathname.startsWith(route))) {
          navigate("/admin", { replace: true });
        }
      }
    }
  }, [
    initialized,
    authenticated,
    userRoles,
    hasAnyRole,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    const prefetchCart = async () => {
      // Chỉ prefetch cart cho role LEARNER
      if (!hasAnyRole([Role.LEARNER])) {
        return;
      }
      try {
        const res = await CartApiService.getCart();
        const courses = res.data?.courses || [];
        localStorage.setItem('cartCache', JSON.stringify(courses));
      } catch {}
    };
    prefetchCart();
  }, [hasAnyRole]);

  // Check if current route is an admin route
  const isAdminRoute =
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/manage/categories") ||
    location.pathname.startsWith("/manage/courses") ||
    location.pathname.startsWith("/manage/orders") ||
    location.pathname.startsWith("/course-builder");

  // Check neu la mentor va dang o mentor screen
  const isMentorRoute = hasAnyRole([Role.STAFF_COURSE_MENTOR]) && location.pathname.startsWith("/mentor/courses");

  // Show loading state while authentication is being checked
  if (loading || !initialized) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <Navbar />
      <main className="main-content">
        <AppRoutes />
      </main>
      {!isAdminRoute && !isMentorRoute && <Footer />}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <LearningProgressProvider>
        <Router>
          <AppContent />
        </Router>
      </LearningProgressProvider>
    </AuthProvider>
  );
};

export default App;
